// ArenaHUD — bottom-left glass panel that controls the Sports Arena.
//
// Contents:
//   • Live scoreboard: PLAYER  X — Y  CPU
//   • Sport toggle:    Basketball | Soccer
//   • Difficulty pills: Easy · Basic · Difficult · Hall of Fame
//   • Reset button
//   • Collapsible on mobile
//
// Kept intentionally compact and left-anchored so it never conflicts with
// the existing right-column controls, the bottom-centred nav pill, or the
// controls hint / audio controls that already live in the bottom-left area.

import React, { useEffect, useState } from "react";
import { Trophy, Volleyball, Zap, RotateCcw, ChevronDown, ChevronUp, Shirt, Users, Handshake, Diamond, MapPin } from "lucide-react";

import { COLORS } from "../constants";
import { panelBox, eyebrow, fontSerif } from "./styles";
import {
  DIFFICULTIES,
  DIFFICULTY_ORDER,
  SPORTS,
  SPORT_ORDER,
  PLAYER_TEAM_OPTIONS,
  FOOTBALL_ROLES,
  FOOTBALL_ROLE_ORDER,
} from "../arena/arenaState";

function sportIcon(id) {
  if (id === "basketball") return <Volleyball size={13} />;
  if (id === "football") return <Diamond size={13} />;
  if (id === "baseball") return <Diamond size={13} />; // gold diamond reads as home plate
  return <Zap size={13} />;
}

export default function ArenaHUD({ arenaApi, compact = false, isTouch = false }) {
  const {
    sport,
    setSport,
    difficulty,
    setDifficulty,
    teamId,
    setTeamId,
    teamColor,
    scores,
    resetScores,
    arenaOpen,
    setArenaOpen,
    lastGoal,
    peerCount = 0,
    readyCount = 0,
    matchInvite,
    setMatchInvite,
    role,
    setRole,
    teleportRef,
  } = arenaApi;

  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (!lastGoal) return;
    setPulse(true);
    const id = setTimeout(() => setPulse(false), 900);
    return () => clearTimeout(id);
  }, [lastGoal]);

  const wrap = {
    position: "absolute",
    left: compact ? 12 : 24,
    // Top-left, tucked directly beneath the BrandBar. BrandBar sits at
    // top:22 and is ~74px tall (bottom edge ≈ 96). We push to top:118 so
    // there is a clear ~20px gutter of breathing room below the header.
    top: compact ? 108 : 118,
    zIndex: 6,
    pointerEvents: "auto",
    // Widths are clamped so the panel never crashes into the right-hand
    // utility stack (which reserves ~200px on narrow viewports, ~220px on
    // wide viewports). On sub-400px screens the panel narrows to fit.
    width: compact
      ? "min(calc(100vw - 200px), 260px)"
      : "min(calc(100vw - 240px), 300px)",
    maxWidth: compact ? 260 : 300,
  };

  const box = {
    ...panelBox,
    padding: compact ? "10px 12px" : "12px 14px",
    color: COLORS.ivory,
    fontFamily: fontSerif,
    boxShadow: pulse
      ? "0 0 0 2px rgba(250,204,21,0.6), 0 12px 30px rgba(0,0,0,0.45)"
      : "0 8px 22px rgba(0,0,0,0.4)",
    transition: "box-shadow .35s ease",
  };

  const headerRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: arenaOpen ? 10 : 0,
  };

  const scoreRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "6px 8px",
    borderRadius: 10,
    background: "rgba(15,31,74,0.55)",
    border: `1px solid ${COLORS.navyMid}`,
    marginBottom: 10,
  };

  const scoreCol = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  };

  const scoreNumber = {
    fontSize: compact ? 22 : 26,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: 1,
    color: COLORS.goldBright,
  };

  const scoreLabel = {
    fontSize: 9,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: "rgba(243,234,212,0.7)",
    marginTop: 3,
  };

  const segRow = {
    display: "flex",
    gap: 6,
    marginBottom: 10,
    flexWrap: "wrap",
  };

  const seg = (active) => ({
    flex: "1 1 auto",
    padding: "6px 10px",
    fontSize: 10,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    fontWeight: 700,
    fontFamily: fontSerif,
    cursor: "pointer",
    borderRadius: 8,
    border: `1px solid ${active ? COLORS.gold : COLORS.navyMid}`,
    background: active
      ? "linear-gradient(180deg, rgba(230,180,80,0.35) 0%, rgba(230,180,80,0.10) 100%)"
      : "rgba(11,21,48,0.5)",
    color: active ? COLORS.goldBright : COLORS.ivory,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    transition: "background .15s, color .15s, border-color .15s",
  });

  const difficultyGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
    marginBottom: 8,
  };

  const resetBtn = {
    ...seg(false),
    flex: "0 0 auto",
    padding: "5px 10px",
    fontSize: 9,
    color: "rgba(243,234,212,0.85)",
  };

  return (
    <div data-testid="arena-hud" style={wrap}>
      <div style={box}>
        {/* Header row: title + score summary + collapse toggle */}
        <div style={headerRow}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Trophy size={14} color={COLORS.gold} />
            <div>
              <div style={{ ...eyebrow, fontSize: 9 }}>Sports Arena</div>
              {!arenaOpen && (
                <div
                  data-testid="arena-hud-collapsed-score"
                  style={{
                    fontSize: 12,
                    color: COLORS.ivory,
                    letterSpacing: 1.2,
                    marginTop: 2,
                  }}
                >
                  {scores.player} · {scores.cpu}
                  <span
                    style={{
                      fontSize: 9,
                      letterSpacing: 2,
                      color: "rgba(243,234,212,0.55)",
                      marginLeft: 8,
                    }}
                  >
                    · {SPORTS[sport].label.toUpperCase()} · {DIFFICULTIES[difficulty].label.toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            data-testid="arena-hud-toggle"
            onClick={() => setArenaOpen(!arenaOpen)}
            style={{
              background: "transparent",
              border: "none",
              color: COLORS.ivory,
              cursor: "pointer",
              padding: 4,
              display: "flex",
              alignItems: "center",
            }}
            aria-label={arenaOpen ? "Collapse arena panel" : "Expand arena panel"}
          >
            {arenaOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>

        {arenaOpen && (
          <>
            {/* Go to Arena — teleport camera + walker to the entrance
                plaza at [40,0,40]. Registered by SportsArena via
                arenaApi.teleportRef. Rendered above the peer indicator
                so it's the first thing a visitor sees when opening the
                panel. */}
            <button
              data-testid="arena-goto-btn"
              onClick={() => {
                const t =
                  teleportRef && teleportRef.current && teleportRef.current.teleport;
                if (typeof t === "function") t();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                width: "100%",
                marginBottom: 10,
                padding: "9px 10px",
                borderRadius: 10,
                cursor: "pointer",
                border: `1px solid ${COLORS.gold}`,
                background: "linear-gradient(180deg,#e6b450 0%,#c8933a 100%)",
                color: "#111827",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                fontFamily: fontSerif,
                boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
              }}
              aria-label="Teleport to the sports arena entrance"
            >
              <MapPin size={13} />
              Go to Arena
            </button>

            {/* Live peer indicator — 'you + N others on the court' */}
            <div
              data-testid="arena-peer-indicator"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
                padding: "5px 10px",
                borderRadius: 8,
                background:
                  peerCount > 0
                    ? "rgba(34,197,94,0.15)"
                    : "rgba(15,31,74,0.35)",
                border: `1px solid ${
                  peerCount > 0 ? "rgba(34,197,94,0.5)" : COLORS.navyMid
                }`,
                fontSize: 10,
                letterSpacing: 1.5,
                color:
                  peerCount > 0 ? "#86efac" : "rgba(243,234,212,0.6)",
                fontFamily: fontSerif,
                textTransform: "uppercase",
              }}
            >
              <Users size={11} />
              <span data-testid="arena-peer-count">
                {peerCount === 0
                  ? "You are alone on the court"
                  : peerCount === 1
                  ? "1 other visitor on the court"
                  : `${peerCount} other visitors on the court`}
              </span>
            </div>

            {/* Match Invite toggle — when ON, remote peers' kicks affect your
                ball, and your kicks are broadcast to peers who also have
                Invite ON. Turns co-presence into head-to-head play. */}
            <button
              data-testid="arena-match-invite-toggle"
              onClick={() => setMatchInvite(!matchInvite)}
              aria-pressed={matchInvite ? "true" : "false"}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                width: "100%",
                marginBottom: 10,
                padding: "7px 10px",
                borderRadius: 8,
                cursor: "pointer",
                border: `1px solid ${matchInvite ? COLORS.gold : COLORS.navyMid}`,
                background: matchInvite
                  ? "linear-gradient(180deg, rgba(230,180,80,0.32), rgba(230,180,80,0.08))"
                  : "rgba(11,21,48,0.5)",
                color: matchInvite ? COLORS.goldBright : COLORS.ivory,
                fontFamily: fontSerif,
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                fontWeight: 700,
                transition: "background .15s, color .15s, border-color .15s",
              }}
              title={
                matchInvite
                  ? "Match Invite ON — remote peers' clicks affect your ball"
                  : "Match Invite OFF — only your clicks affect your ball"
              }
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Handshake size={13} /> Match Invite
                {readyCount > 0 && (
                  <span
                    data-testid="arena-ready-badge"
                    style={{
                      marginLeft: 4,
                      padding: "1px 6px",
                      borderRadius: 999,
                      background: "rgba(34,197,94,0.9)",
                      color: "#0b1530",
                      fontSize: 8.5,
                      letterSpacing: 1.2,
                      fontWeight: 800,
                      lineHeight: 1.3,
                      boxShadow: "0 0 8px rgba(34,197,94,0.5)",
                    }}
                    title={`${readyCount} other visitor${
                      readyCount === 1 ? "" : "s"
                    } ready to play`}
                  >
                    {readyCount} READY
                  </span>
                )}
              </span>
              <span
                data-testid="arena-match-invite-state"
                style={{
                  padding: "1px 8px",
                  borderRadius: 999,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  background: matchInvite ? COLORS.goldBright : "rgba(255,255,255,0.08)",
                  color: matchInvite ? "#0b1530" : "rgba(243,234,212,0.65)",
                  border: matchInvite
                    ? "none"
                    : "1px solid rgba(255,255,255,0.12)",
                }}
              >
                {matchInvite ? "ON" : "OFF"}
              </span>
            </button>

            {/* Scoreboard */}
            <div style={scoreRow} data-testid="arena-scoreboard">
              <div style={scoreCol}>
                <div style={scoreNumber} data-testid="arena-score-player">
                  {scores.player}
                </div>
                <div style={scoreLabel}>You</div>
              </div>
              <div
                style={{
                  fontFamily: fontSerif,
                  fontSize: 11,
                  color: "rgba(243,234,212,0.55)",
                  letterSpacing: 2,
                }}
              >
                VS
              </div>
              <div style={scoreCol}>
                <div style={scoreNumber} data-testid="arena-score-cpu">
                  {scores.cpu}
                </div>
                <div style={scoreLabel}>CPU</div>
              </div>
            </div>

            {/* Sport toggle */}
            <div style={{ ...eyebrow, marginBottom: 5 }}>Sport</div>
            <div style={segRow} data-testid="arena-sport-toggle">
              {SPORT_ORDER.map((id) => (
                <button
                  key={id}
                  data-testid={`arena-sport-${id}`}
                  onClick={() => setSport(id)}
                  style={seg(sport === id)}
                >
                  {sportIcon(id)}
                  {SPORTS[id].label}
                </button>
              ))}
            </div>

            {/* Difficulty */}
            <div style={{ ...eyebrow, marginBottom: 5 }}>Difficulty</div>
            <div style={difficultyGrid} data-testid="arena-difficulty-grid">
              {DIFFICULTY_ORDER.map((id) => (
                <button
                  key={id}
                  data-testid={`arena-difficulty-${id}`}
                  onClick={() => setDifficulty(id)}
                  style={seg(difficulty === id)}
                  title={DIFFICULTIES[id].tagline}
                >
                  {DIFFICULTIES[id].label}
                </button>
              ))}
            </div>

            {/* Role picker — football only. Selecting WR/RB/DB puts the
                visitor in control of a specific NPC and turns the QB into
                a CPU that throws to whichever position you picked. */}
            {sport === "football" && (
              <>
                <div style={{ ...eyebrow, marginBottom: 5 }}>Role</div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                    gap: 5,
                    marginBottom: 8,
                  }}
                  data-testid="arena-role-grid"
                >
                  {FOOTBALL_ROLE_ORDER.map((rid) => {
                    const meta = FOOTBALL_ROLES.find((x) => x.id === rid);
                    return (
                      <button
                        key={rid}
                        data-testid={`arena-role-${rid}`}
                        onClick={() => setRole(rid)}
                        style={seg(role === rid)}
                        title={meta.tagline}
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
                {role !== "qb" && (
                  <div
                    style={{
                      fontSize: 10,
                      color: "rgba(243,234,212,0.65)",
                      fontFamily: fontSerif,
                      lineHeight: 1.4,
                      marginBottom: 10,
                      padding: "5px 8px",
                      borderRadius: 6,
                      background: "rgba(11,21,48,0.5)",
                      border: `1px solid ${COLORS.navyMid}`,
                    }}
                    data-testid="arena-role-tagline"
                  >
                    {FOOTBALL_ROLES.find((r) => r.id === role)?.tagline}
                    <br />
                    <span style={{ color: COLORS.goldBright }}>
                      Use WASD or arrow keys to move.
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Team picker */}
            <div
              style={{
                ...eyebrow,
                marginBottom: 5,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Shirt size={11} color={teamColor} /> Your Team
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 10,
                alignItems: "center",
              }}
              data-testid="arena-team-picker"
            >
              {PLAYER_TEAM_OPTIONS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`arena-team-${t.id}`}
                  onClick={() => setTeamId(t.id)}
                  aria-label={`Team ${t.label}`}
                  title={`Team ${t.label}`}
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    border:
                      teamId === t.id
                        ? `2.5px solid ${COLORS.goldBright}`
                        : `2px solid rgba(255,255,255,0.15)`,
                    background: t.color,
                    cursor: "pointer",
                    padding: 0,
                    boxShadow:
                      teamId === t.id
                        ? `0 0 0 3px rgba(230,180,80,0.25), 0 2px 6px rgba(0,0,0,0.35)`
                        : "0 2px 6px rgba(0,0,0,0.35)",
                    transition: "box-shadow .15s, border-color .15s",
                  }}
                />
              ))}
              <div
                style={{
                  marginLeft: 4,
                  fontSize: 10,
                  color: "rgba(243,234,212,0.65)",
                  fontFamily: fontSerif,
                }}
                data-testid="arena-team-label"
              >
                {PLAYER_TEAM_OPTIONS.find((t) => t.id === teamId)?.label ||
                  "Royal"}
              </div>
            </div>

            {/* Reset */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(243,234,212,0.6)",
                  fontFamily: fontSerif,
                  lineHeight: 1.4,
                }}
              >
                {DIFFICULTIES[difficulty].tagline}. Walk close to the ball, then click to shoot.
              </div>
              <button
                data-testid="arena-reset"
                onClick={resetScores}
                style={resetBtn}
                title="Reset session scores"
              >
                <RotateCcw size={11} /> Reset
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
